
import {
  defineComponent,
  computed,
  watch,
  onMounted,
  ref,
  nextTick,
} from "vue";
import "@fullcalendar/core/vdom";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/vue3";
import { Draggable } from "@fullcalendar/interaction";
import AddScheduleModal from "@/components/modals/forms/AddScheduleModal.vue";
import { ScheduleApi } from "@/services/api/Schedule.service";
import { TranslationService } from "@/core/services/TranslationService";
import { FormApi } from "@/services/api/Form.service";
import { config } from "./schedularConfig";
import VZFilter from "@/components/kt-datatable/components/VZFilter.vue";
import FormDetailModal from "@/views/form/modal/FormDetailModal.vue";
import { IForm } from "@/interfaces/iForm";
import { useToast } from "vue-toastification";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { IDataTableFilterOption } from "@/interfaces/iPageModel";
import { setDateRangeShortcuts } from "@/components/typography/DateRangeOptions";
import { FormatMethods, getTimeZoneList } from "@/helpers/data-format";
import { GET_SELECTED_SITES_STR, SITE_STORE } from "@/store/userSite/types";
import { UserAPI } from "@/services/api/User.service";
import { SiteApi } from "@/services/api/Site.service";
import { hasPermission } from "@/core/services/permission.service";
import store from "@/store";
import { Actions } from "@/store/enums/StoreEnums";
import { v4 as uuidv4 } from "uuid";
import { usePopover, useSite } from "./schedularComposable";
import { MESSAGE_STORE, SMS_DATA_CHANGED } from "@/store/message/types";
import { parseDate } from "element-plus";
export default defineComponent({
  components: {
    FullCalendar,
    "vz-add-schedule": AddScheduleModal,
    "vz-filter": VZFilter,
    FormDetailModal,
  },
  setup() {
    const t = (key) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const { formatArgs, hidePopovers, initPopovers, getClass } = usePopover();
    const { form, sites, isValidEvent } = useSite();
    const sitesSearch = ref<any>({});
    const forms = ref<any>({});
    const scheduleFormRef = ref<HTMLFormElement>();
    const searchTerm = ref();
    const searchTermEvent = ref("");
    const shortcuts = ref(setDateRangeShortcuts());
    const filters = ref();
    const editData = ref<any>({});
    const show = ref<any>(false);
    const isEditable = ref<any>(true);
    const calendarOptions = ref<any>(config());
    const showDetailDialog = ref<any>(false);
    const selectedForm = ref<Partial<IForm>>({});
    const scheduleSuccessMsg = t("pages.scheduler.scheduleSuccessMsg");
    const editScheduledMsg = t("pages.scheduler.editScheduledMsg");
    const eventDenyMsg = t("pages.scheduler.cannotCreateTaskForDate");
    const invalidFormSceduleMsg = t("validation.invalidFormSchedule");
    const calendarRef = ref();
    const loading = ref(false);
    const users = ref<any>({});
    const childComponentRef = ref<null | HTMLElement>();

    const scheduleFilter = ref<Array<IDataTableFilterOption>>([
      {
        key: "siteIds",
        label: TranslationService.translate("common.sites"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: "site/filter/SITES",
        textKey: "text",
        valueKey: "value",
      },
      {
        key: "startDate",
        label: TranslationService.translate("common.selectDate"),
        type: "date",
      },
      {
        key: "formDefinitionIds",
        label: TranslationService.translate("pages.form.title"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: "forms/filter/FORM_NAME",
        textKey: "text",
        valueKey: "value",
      },
      {
        key: "userIds",
        label: TranslationService.translate("common.users"),
        type: "lookup",
        attrs: {
          mode: "tags",
        },
        url: "security/filter/USERS",
        textKey: "text",
        valueKey: "value",
      },
    ]);
    const selectedValue = ref([
      moment().subtract(1, "days").format(),
      moment().format(),
    ]);
    const timeZoneList = computed(() => getTimeZoneList());

    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.scheduler.scheduler"), [
        "Home",
        t("pages.scheduler.scheduler"),
      ]);
      getDataFromServer();
    });
    const getDataFromServer = async () => {
      await Promise.all([
        SiteApi.getInstance().getScheduleSite({
          size: 100000,
        }),
        UserAPI.getInstance().getUserList({
          size: 100000,
          status: "ACTIVE",
        }),
        loadForm({ size: 10000, status: "ACTIVE" }),
        loadSchedullingData(),
      ])
        .then((values) => {
          sites.value = values[0].content;
          sites.value.sort((a, b) => (a.name > b.name ? 1 : -1));
          users.value = values[1].content;
          users.value.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));
        })
        .catch((err) => {
          console.error(err);
        });

      calendarOptions.value.dateClick = (e) => {
        if (hasPermission("VYZOR:APPLICATIONS:SCHEDULER:CREATE")) {
          hidePopovers();
          editData.value = {};
          show.value = false;
          isEditable.value = true;
          const { siteId } = e.resource._resource.extendedProps;
          editData.value.start = e.dateStr;
          editData.value.end = moment(e.dateStr).add(30, "minute").format();
          editData.value.resourceId = e?.resource?._resource.id;
          editData.value.siteId = siteId;
          editData.value.assignUserId = `${
            e?.resource?._resource.id.split("_")[1]
          }`;
          editData.value.title = "";
          // scheduleFormRef?.value?.resetForm();
          show.value = true;
        }
      };
      calendarOptions.value.eventAllow = (dropLocation, draggedEvent) => {
        const formId = draggedEvent.id;
        const siteId = dropLocation.resource._resource.id.split("_")[0];
        const userId = dropLocation.resource._resource.id.split("_")[1];
        form.value = forms.value.filter((form) => form.webId == formId)[0];
        return isValidEvent(formId, siteId, userId);
      };

      calendarOptions.value.customButtons = {
        prev: {
          text: "Prev",
          click: function () {
            calendarRef.value.getApi().prev();
            refresh();
          },
        },
        next: {
          text: "Next",
          click: function () {
            calendarRef.value.getApi().next();
            refresh();
          },
        },
        today: {
          text: "Today",
          click: function () {
            calendarRef.value.getApi().today();
            refresh();
          },
        },
        resourceTimelineWeek: {
          text: "Week",
          click: function () {
            calendarRef.value.getApi().changeView("resourceTimelineWeek");
            refresh();
          },
        },
        resourceTimelineDay: {
          text: "Day",
          click: function () {
            calendarRef.value.getApi().changeView("resourceTimelineDay");
            refresh();
          },
        },
      };

      calendarOptions.value.eventClick = (e) => {
        if (hasPermission("VYZOR:APPLICATIONS:SCHEDULER:UPDATE")) {
          hidePopovers();
          editData.value = {};
          editData.value = calendarOptions.value.events.find(
            (obj) => obj.identifier == e.event.extendedProps.identifier
          );
          if (editData.value.type == "CRON") {
            isEditable.value = false;
          }
          scheduleFormRef?.value?.resetForm();
          show.value = true;
        }
      };
      calendarOptions.value.eventMouseEnter = (arg) => {
        const { siteName, formName, assignUserId } = arg.event.extendedProps;
        const user = users.value.find((user) => user.webId == assignUserId);
        formatArgs({
          id: arg.event.id,
          title: formName ?? "",
          user: user?.firstName + " " + user?.lastName,
          site: siteName,
          form: formName,
          startStr: arg.event.startStr,
          endStr: arg.event.endStr,
          allDay: arg.event.allDay,
          notesCount: arg.event.extendedProps.notesCount,
          conversationCount: arg.event.extendedProps.chatCount,
        });
        // Show popover preview
        initPopovers(arg.el);
      };
      calendarOptions.value.eventMouseLeave = (arg) => {
        hidePopovers();
      };

      calendarOptions.value.eventDragStart = (obj) => {
        editData.value.id = obj?.event?._def?.publicId;
      };
      calendarOptions.value.eventDrop = (obj) => {
        let eventData = {} as any;
        eventData.startDate = moment(
          obj?.event?._instance?.range?.start
        ).format();
        eventData.endDate = moment(obj?.event?._instance?.range?.end)
          .add(30, "minute")
          .format();
        eventData.userAccountId =
          obj?.event?._def?.resourceIds[0].split("_")[1];
        eventData.siteId = obj?.event?._def?.resourceIds[0].split("_")[0];
        eventData.formDefinitionId = obj?.event?._def?.extendedProps.formDefId;
        eventData.webId = editData.value.id;
        updateData(eventData);
      };
      let isDropInProgress = false;

      calendarOptions.value.drop = (e) => {
        if (isDropInProgress) {
          e.remove();
          return false;
        }

        // Set the flag to indicate a drop is in progress
        isDropInProgress = true;

        const user = users.value.find(
          (user) => user.webId == e.resource._resource.extendedProps.userId
        );

        if (moment() > moment(e.dateStr)) {
          toast.error(eventDenyMsg);
          e.remove();
          isDropInProgress = false; // Reset the flag immediately on denial
          return false;
        } else {
          editData.value = {};
          const { siteId } = e.resource._resource.extendedProps;
          editData.value.selectedSite = sites.value.find(
            (site) => site.webId == siteId
          );
          editData.value.start = e.dateStr;
          editData.value.end = moment(e.dateStr).add(30, "minute").format();
          editData.value.resourceId = e?.resource?._resource.id;
          editData.value.id = e.draggedEl.id;
          editData.value.siteId = siteId;
          editData.value.pnUuid = user?.pnUuid;
          editData.value.documentName = e.draggedEl.innerText;

          // Create the event
          createEvent(editData.value);
          setTimeout(() => {
            isDropInProgress = false;
          }, 5000);

          return false;
        }
      };

      // await loadForm({ size: 10000, status: "ACTIVE" });
      new Draggable(
        document.getElementById("draggable-events") as HTMLElement,
        {
          itemSelector: ".drop-event",
          eventData: function (eventEl) {
            return {
              title: eventEl.innerText,
              id: eventEl.id,
            };
          },
        }
      );
    };

    const createEvent = (data) => {
      const userAccountId = data.resourceId.split("_")[1];
      let eventData = {} as any;
      eventData.status = "SCHEDULE"; // Required by backend
      eventData.documentName = data.documentName ?? "";
      eventData.type = null; // Required by backend(Hamza changed type=100 to null)
      eventData.fileId = null;
      eventData.flow = null;
      eventData.latitude = null;
      eventData.longitude = null;
      eventData.timeFrame = null;
      eventData.roleId = data.roleId;
      eventData.formScheduleId = null;
      eventData.completedDate = null;
      eventData.selectedSite = data.selectedSite;
      eventData.startDate = moment(data.start).format("YYYY-MM-DDTHH:mm:ss");
      eventData.endDate = moment(data.end)
        .subtract(1, "millisecond")
        .format("YYYY-MM-DDTHH:mm:ss");
      eventData.userAccountId = userAccountId;
      eventData.siteId = data.siteId;
      eventData.formDefinitionId = data.id;
      eventData.pnUuid = data.pnUuid;
      submitData(eventData);
    };
    const loadSchedullingData = async () => {
      try {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        calendarRef.value.getApi().removeAllEvents();
        calendarOptions.value.events = [];
        await getAllScheduleDocuments();
      } catch (error) {
        //ERROR
      } finally {
        store.dispatch(`${MESSAGE_STORE}/${SMS_DATA_CHANGED}`);
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };

    const getAllScheduleDocuments = async () => {
      // get end date from currentEnd
      const endDate = calendarRef.value.getApi().view.currentEnd;
      // minus 1 millisecond from end date
      const endDateMinusOneMillisecond = moment(endDate)
        .subtract(1, "millisecond")
        .format("YYYY-MM-DDTHH:mm:ssZ");
      await ScheduleApi.getInstance()
        .getAllScheduleDocuments({
          startDate: moment(calendarRef.value.getApi().view.currentStart)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ"),
          endDate: endDateMinusOneMillisecond,
          ...filters.value,
          search: searchTermEvent.value,
        })
        .then(async (data) => {
          const siteIds = [] as any;
          if (
            searchTermEvent.value !== "" &&
            searchTermEvent.value !== undefined
          ) {
            data.map((resp) => {
              if (siteIds.filter((id) => resp.siteId == id).length == 0) {
                siteIds.push(resp.siteId);
              }
            });
            await ScheduleApi.getInstance()
              .getResourceList({ size: 10000 })
              .then((resourceData) => {
                // const resoData = [] as any;
                // resourceData.content.map((reData) => {
                //   if (siteIds.filter((id) => reData.webId == id).length > 0) {
                //     resoData.push({ ...reData });
                //   }
                // });
                let resources = prepareResources(resourceData.content, data);
                calendarOptions.value.resources = resources;
              })
              .catch((error) => {
                // ERROR
              })
              .finally(() => {
                // TODO
              });
          } else {
            await loadResourcelistData();
          }

          let events = prepareEvents(data);
          calendarOptions.value.events = [];
          calendarOptions.value.events =
            calendarOptions.value.events.concat(events);
        })
        .catch((error) => {
          // ERROR
        })
        .finally(() => {
          //TODO
        });
    };
    const loadResourcelistData = async () => {
      await ScheduleApi.getInstance()
        .getResourceList({
          size: 10000,
          ...filters.value,
        })
        .then((data) => {
          let resources = prepareResources(data.content, null);
          calendarOptions.value.resources = resources;
        })
        .catch((error) => {
          // ERROR
        })
        .finally(() => {
          // TODO
        });
    };
    const refresh = async () => {
      await Promise.all([loadSchedullingData()]);
    };

    const deleteTask = async (data) => {
      await FormApi.getInstance()
        .deleteDocument(data.webId)
        .then(() => {
          toast.success("Task has been deleted sucessfully.");
        })
        .catch(() => {
          toast.error("Error deleting task");
        })
        .finally(async () => {
          // const notificationPayload = {
          //   message: {
          //     topic: data?.pnUuid,
          //     notification: {
          //       title: "Vyzor Notification",
          //       body: `${data.documentName} is no longer on your list. You're all set!`,
          //     },
          //     data: {
          //       story_id: "story_12345",
          //     },
          //   },
          // };
          // await ScheduleApi.getInstance().sendNotification(notificationPayload);
          const element = document.getElementById("closeButtonH");
          if (element) {
            element.click();
          }
          show.value = false;
          getDataFromServer();
        });
    };

    const loadForm = async (params) => {
      await FormApi.getInstance()
        .getFormList(params)
        .then((res) => {
          forms.value = res.content;
        })
        .catch((err) => {
          console.error(err);
        });
    };

    const searchData = async () => {
      await loadForm({
        size: 10000,
        status: "ACTIVE",
        search: searchTerm.value,
      });
    };
    const prepareResources = (resources, allEventsData) => {
      let parsedData: any = [];
      resources.map((data) => {
        data.roles.map((role) => {
          parsedData.push({
            id: data.webId + "_" + role.userId,
            building: data.siteName,
            //building: data.siteName + " - " + data.webId,// width webid
            title: role.firstName + " " + role.lastName,
            siteId: data.webId,
            userId: role.userId,
            roleId: role.roleId,
            startDate: moment(calendarRef.value.getApi().getDate())
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ"),
            endDate: moment(calendarRef.value.getApi().getDate())
              .endOf("day")
              .add(1, "second")
              .format("YYYY-MM-DDTHH:mm:ssZ"),
          });
        });
      });
      if (searchTermEvent.value.length < 3) {
        return parsedData;
      }
      const containsAlphabet = /[a-zA-Z]/.test(searchTermEvent.value);
      if (!containsAlphabet) {
        return parsedData;
      }
      let searchUsers = [];
      let searchItems = [];
      let searchSites = [];
      if (searchTermEvent.value.length > 2) {
        searchUsers = parsedData.filter((obj) =>
          obj.title.toLowerCase().includes(searchTermEvent.value.toLowerCase())
        );
        if (allEventsData) {
          searchItems = parsedData.filter((parsedObj) => {
            if (
              allEventsData.some(
                (eventObj) =>
                  eventObj.formName
                    .toLowerCase()
                    .includes(searchTermEvent.value.toLowerCase()) &&
                  parsedObj.siteId == eventObj.siteId
              )
            ) {
              return true;
            } else {
              return false;
            }
          });
          searchSites = parsedData.filter((obj) =>
            obj.building
              .toLowerCase()
              .includes(searchTermEvent.value.toLowerCase())
          );
        }
      } else {
        return parsedData;
      }
      return [...searchUsers, ...searchItems, ...searchSites];
    };

    const submitData = async (data) => {
      if (moment() > moment(data.startDate)) {
        toast.error(eventDenyMsg);
        return false;
      }
      const diff = moment(data.endDate).diff(data.startDate, "minutes");
      if (diff < 29) {
        toast.error("Event cannot be scheduled for less than 30 mins");
        return false;
      }
      form.value = forms.value.filter(
        (form) => form.webId == data.formDefinitionId
      )[0];
      // if (
      //   !isValidEvent(data.formDefinitionId, data.siteId, data.userAccountId)
      // ) {
      //   toast.error(invalidFormSceduleMsg);
      //   return;
      // }
      if (data.roleId !== null && data.roleId !== undefined) {
        // we only need one from roleId and userAccountId
        data.userAccountId = null;
      }

      const selectedTimeZone: any = timeZoneList.value.find(
        (timezone) => timezone.value == data?.selectedSite?.timezone
      );

      data.startDate = `${data.startDate}${selectedTimeZone.offset}`;
      data.endDate = `${data.endDate}${selectedTimeZone.offset}`;

      loading.value = true;
      await ScheduleApi.getInstance()
        .createSchedule(data)
        .then(async (res) => {
          toast.success(scheduleSuccessMsg);
          await loadSchedullingData();
          nextTick(() => {
            const calendar = calendarRef.value?.getApi();
            if (calendar && data.startDate) {
              const timePart = moment(data.startDate).format("HH:mm:ss");
              calendar.scrollToTime(timePart);
            }
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const updateData = async (data) => {
      if (data.roleId) {
        data.userAccountId = data.assignUserId;
      }
      if (moment(data.endDate).diff(moment(), "minutes") < 0) {
        toast.error(eventDenyMsg);
        return false;
      }
      form.value = forms.value.filter(
        (form) => form.webId == data.formDefinitionId
      )[0];
      // if (
      //   !isValidEvent(data.formDefinitionId, data.siteId, data.userAccountId)
      // ) {
      //   toast.error(invalidFormSceduleMsg);
      //   return;
      // }

      data.selectedSite = sites.value.find((site) => site.webId == data.siteId);
      const selectedTimeZone: any = timeZoneList.value.find(
        (timezone) => timezone.value == data?.selectedSite?.timezone
      );

      data.startDate = `${data.startDate}${selectedTimeZone.offset}`;
      data.endDate = `${data.endDate}${selectedTimeZone.offset}`;

      loading.value = true;
      await ScheduleApi.getInstance()
        .updateSchedule(data)
        .then(async (res) => {
          toast.success(editScheduledMsg);
          loadSchedullingData();
          loading.value = false;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          //TODO
        });
    };

    const openDetailModal = async (formWebId) => {
      showDetailDialog.value = true;
      selectedForm.value = {};
      const form = await FormApi.getInstance().getFormById(formWebId);
      selectedForm.value = form;
    };

    const showModal = () => {
      editData.value = {};
      scheduleFormRef?.value?.resetForm();
      show.value = true;
    };

    const prepareEvents = (data) => {
      return data.map((obj) => {
        return {
          id: obj.documentId,
          formDefId: obj.formDefinitionId,
          resourceId: obj.siteId + "_" + obj.assignUserId,
          start: FormatMethods.convertToLocalTimeZone(obj.startDate),
          end: FormatMethods.convertToLocalTimeZone(obj.endDate),
          title: obj.formName, // Assuming you decided to remove the commented title line
          classNames: getClass(obj.scheduleType),
          identifier: uuidv4(),
          ...obj,
        };
      });
    };

    const applyFilter = (updatedFilters) => {
      if (!filters.value && Object.keys(updatedFilters).length === 0) {
        return;
      }
      if (filters.value && filters.value.startDate) {
        calendarRef.value.getApi().today();
      }
      if (updatedFilters && updatedFilters.startDate) {
        let startDateTemp = updatedFilters.startDate;
        updatedFilters.startDate = moment(updatedFilters.startDate)
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ssZ");
        const formattedDate = moment(startDateTemp)
          .endOf("day")
          .utcOffset(5)
          .format("YYYY-MM-DDTHH:mm:ssZ");
        updatedFilters.endDate = formattedDate;
        calendarRef.value.getApi().gotoDate(startDateTemp);
      }
      filters.value = { ...updatedFilters };
      refresh();
    };

    const selectedSites = computed(() => {
      const key = `${SITE_STORE}/${GET_SELECTED_SITES_STR}`;
      return store.getters[key] as string;
    });

    watch(selectedSites, async () => {
      await Promise.all([
        loadSchedullingData(),
        loadForm({
          size: 10000,
          status: "ACTIVE",
          search: searchTerm.value,
        }),
      ]);
    });
    function changeRoute() {
      /// router.push({ name: "schedule" });
    }
    return {
      timeZoneList,
      changeRoute,
      calendarOptions,
      forms,
      shortcuts,
      searchTerm,
      searchTermEvent,
      searchData,
      selectedValue,
      submitData,
      editData,
      updateData,
      showModal,
      scheduleFilter,
      applyFilter,
      calendarRef,
      loading,
      refresh,
      selectedForm,
      openDetailModal,
      show,
      hasPermission,
      showDetailDialog,
      isEditable,
      getDataFromServer,
      deleteTask,
      childComponentRef,
      t,
    };
  },
});
